/**
 * resolveEffectiveAccess() — Single source of truth for ALL access decisions.
 *
 * Every page, route guard, and component MUST consume this engine's output
 * instead of implementing its own access logic.
 *
 * Role priority (highest wins):
 *   1. blocked
 *   2. admin
 *   3. institutional_active
 *   4. cohort_active (institutional with program not yet started)
 *   5. paid_active
 *   6. authenticated_free
 *   7. guest
 */

import { addSentryBreadcrumb } from "@/lib/sentry";

// ─── Types ───────────────────────────────────────────────────────────────────

export type EffectiveStatus = "loading" | "ready" | "denied";

export type EffectiveRole =
  | "guest"
  | "free"
  | "paid"
  | "institutional"
  | "cohort"
  | "admin"
  | "blocked";

export interface EffectiveAccessResult {
  status: EffectiveStatus;
  role: EffectiveRole;
  canAccessDashboard: boolean;
  canAccessInstitutionalDashboard: boolean;
  canTakeQuiz: boolean;
  canSeePricing: boolean;
  redirectTo: string | null;
  reason: string;
}

// ─── Input ───────────────────────────────────────────────────────────────────

export interface EffectiveAccessInput {
  // Auth layer
  authLoading: boolean;
  user: { id: string; email_confirmed_at?: string | null } | null;
  session: unknown | null;

  // Profile layer
  profileLoading: boolean;
  isBlocked: boolean;
  hasFreeAccess: boolean;
  /** True when institutional user hasn't set their password yet */
  needsPasswordSetup: boolean;

  // Admin layer
  adminLoading: boolean;
  isAdmin: boolean;

  // Subscription layer
  subscriptionLoaded: boolean;
  hasActiveSubscription: boolean;
  /** From SubscriptionContext.hasCohortAccess (fast check) */
  hasCohortAccess: boolean;

  // Institutional layer (detailed)
  institutionalLoading: boolean;
  isInstitutionalStudent: boolean;
  isProgramActive: boolean;
  isProgramNotStarted: boolean;
  cohortEnded: boolean;
  isBaselineRequired: boolean;
  hasBaseline: boolean;
  // Weekly-compliance gate retired 2026-07: only academic level progression
  // controls access. Compliance data remains available as an informational
  // metric for admin reports, never as an access decision input.
  statusReliability: "confirmed" | "degraded" | "unknown";

  // Current route (for redirect decisions)
  pathname: string;

  // Exam safety valve
  examInProgress?: boolean;
}

function isRealExamRoute(pathname: string): boolean {
  return pathname === "/real-exam" || pathname.startsWith("/real-exam/");
}

// ─── Constants ───────────────────────────────────────────────────────────────

const LOADING_RESULT: EffectiveAccessResult = {
  status: "loading",
  role: "guest",
  canAccessDashboard: false,
  canAccessInstitutionalDashboard: false,
  canTakeQuiz: false,
  canSeePricing: false,
  redirectTo: null,
  reason: "loading",
};

// ─── Engine ──────────────────────────────────────────────────────────────────

export function resolveEffectiveAccess(input: EffectiveAccessInput): EffectiveAccessResult {
  const {
    authLoading,
    user,
    session,
    profileLoading,
    isBlocked,
    hasFreeAccess,
    adminLoading,
    isAdmin,
    subscriptionLoaded,
    hasActiveSubscription,
    hasCohortAccess,
    institutionalLoading,
    isInstitutionalStudent,
    isProgramActive,
    isProgramNotStarted,
    cohortEnded,
    isBaselineRequired,
    hasBaseline,
    statusReliability,
    pathname,
    examInProgress = false,
  } = input;
  const isRealExam = isRealExamRoute(pathname);

  // ── Exam safety valve: never interrupt an active exam ──
  if (examInProgress && user) {
    return {
      status: "ready",
      role: "paid", // treat as fully entitled during exam
      canAccessDashboard: true,
      canAccessInstitutionalDashboard: false,
      canTakeQuiz: true,
      canSeePricing: false,
      redirectTo: null,
      reason: "exam_in_progress_bypass",
    };
  }

  // ── Loading gates (blocking) ──
  if (authLoading) return { ...LOADING_RESULT, reason: "auth_loading" };
  if (!user || !session) {
    // Guest — not authenticated
    const isAuthRoute = pathname.startsWith("/auth/");
    return {
      status: "denied",
      role: "guest",
      canAccessDashboard: false,
      canAccessInstitutionalDashboard: false,
      canTakeQuiz: false,
      canSeePricing: true,
      redirectTo: isAuthRoute ? null : `/auth/login${pathname !== "/" ? `?returnTo=${encodeURIComponent(pathname)}` : ""}`,
      reason: "not_authenticated",
    };
  }

  // Email confirmation gate REMOVED (2026-06): auto_confirm_email=true en Supabase.
  // El email de bienvenida sigue saliendo en background pero nunca bloquea acceso.
  // Usuarios legacy sin email_confirmed_at tampoco quedan atrapados.

  // Password setup required (institutional invite not yet activated)
  if (input.needsPasswordSetup) {
    const isSetPasswordRoute = pathname === "/auth/set-password";
    console.warn("🔑 [AccessEngine] Redirecting to /auth/set-password — needsPasswordSetup=true", {
      userId: user.id,
      pathname,
      alreadyOnSetPassword: isSetPasswordRoute,
    });
    return {
      status: "denied",
      role: "guest",
      canAccessDashboard: false,
      canAccessInstitutionalDashboard: false,
      canTakeQuiz: false,
      canSeePricing: false,
      redirectTo: isSetPasswordRoute ? null : "/auth/set-password",
      reason: "needs_password_setup",
    };
  }

  if (adminLoading) return { ...LOADING_RESULT, reason: "admin_loading" };
  if (!subscriptionLoaded) return { ...LOADING_RESULT, reason: "subscription_loading" };

  // ── Priority 1: Blocked ──
  if (isBlocked) {
    return {
      status: "denied",
      role: "blocked",
      canAccessDashboard: false,
      canAccessInstitutionalDashboard: false,
      canTakeQuiz: false,
      canSeePricing: false,
      redirectTo: "/auth/login",
      reason: "user_blocked",
    };
  }

  // ── Priority 2: Admin ──
  if (isAdmin) {
    return {
      status: "ready",
      role: "admin",
      canAccessDashboard: true,
      canAccessInstitutionalDashboard: false,
      canTakeQuiz: true,
      canSeePricing: false,
      redirectTo: null,
      reason: "admin",
    };
  }

  // ── CRITICAL GUARD: If SubscriptionContext knows about cohort access but
  //    the detailed institutional hook hasn't resolved yet, ALWAYS wait.
  //    This prevents misclassifying cohort users as "paid" or "free"
  //    due to race conditions between SubscriptionContext and useInstitutionalStudent.
  //
  //    Affected user class: ANY user with an active cohort enrollment where
  //    SubscriptionContext resolves before useInstitutionalStudent. Without this
  //    guard, they'd be classified as "paid" (losing institutional dashboard)
  //    or "free" (losing all access) depending on timing. ──
  if (institutionalLoading && hasCohortAccess) {
    return { ...LOADING_RESULT, reason: "institutional_loading_with_cohort_access" };
  }

  // ── Priority 3: Institutional Active ──
  const institutionalConfirmed = isInstitutionalStudent && isProgramActive && !cohortEnded;
  const institutionalDegraded = isInstitutionalStudent && statusReliability === "degraded";
  const institutionalPreProgram = isInstitutionalStudent && isProgramNotStarted && !cohortEnded;
  const institutionalFallback = !isInstitutionalStudent && hasCohortAccess && !cohortEnded;

  if (institutionalConfirmed || institutionalDegraded) {
    // New institutional flow (2026-06): baseline is disabled and weekly compliance
    // no longer blocks module practice. Progression gate is: complete L1 in all 7
    // modules → unlock Real Exam → unlock next level. See mem://architecture/
    // baseline-gate-disabled-institutional-flow.
    return {
      status: "ready",
      role: "institutional",
      canAccessDashboard: true,
      canAccessInstitutionalDashboard: true,
      canTakeQuiz: true,
      canSeePricing: false,
      redirectTo: null,
      reason: institutionalDegraded ? "institutional_degraded_safe" : "institutional_active",
    };
  }

  // ── Priority 4: Cohort Active (pre-program or fallback from subscription context) ──
  if (institutionalPreProgram) {
    return {
      status: "ready",
      role: "cohort",
      canAccessDashboard: true,
      canAccessInstitutionalDashboard: true,
      canTakeQuiz: false,
      canSeePricing: false,
      redirectTo: null,
      reason: "cohort_pre_program",
    };
  }

  if (institutionalFallback) {
    // Detailed hook finished but didn't confirm institutional — use cohort fallback
    return {
      status: "ready",
      role: "cohort",
      canAccessDashboard: true,
      canAccessInstitutionalDashboard: true,
      canTakeQuiz: true,
      canSeePricing: false,
      redirectTo: null,
      reason: "cohort_subscription_context_fallback",
    };
  }

  // Institutional loading while no other access — block to prevent pricing flash
  if (institutionalLoading && !hasActiveSubscription && !hasFreeAccess) {
    return { ...LOADING_RESULT, reason: "institutional_loading_blocking_paywall" };
  }

  // Cohort ended — fall through to personal subscription
  if (isInstitutionalStudent && cohortEnded) {
    // Don't return yet — check paid/free below
  }

  // ── Priority 5: Paid Active ──
  if (hasActiveSubscription || hasFreeAccess) {
    return {
      status: "ready",
      role: "paid",
      canAccessDashboard: true,
      canAccessInstitutionalDashboard: false,
      canTakeQuiz: true,
      canSeePricing: false,
      redirectTo: null,
      reason: hasFreeAccess ? "free_access_profile" : "active_subscription",
    };
  }

  // ── Priority 6: Authenticated Free ──
  return {
    status: "ready",
    role: "free",
    canAccessDashboard: true,
    canAccessInstitutionalDashboard: false,
    canTakeQuiz: false, // no entitlement → no study content at all (2026-08)
    canSeePricing: true,
    redirectTo: null,
    reason: "authenticated_free",
  };
}

// ─── Observability ───────────────────────────────────────────────────────────

export function logEffectiveAccess(
  component: string,
  result: EffectiveAccessResult,
  extra?: Record<string, unknown>
) {
  const label = result.status === "denied" ? "DENIED" : result.status === "loading" ? "LOADING" : "GRANTED";
  const logData = { ...result, ...extra };

  console.log(`🔐 [${component}] ${label} role=${result.role} reason=${result.reason}`, JSON.stringify(logData, null, 0));

  addSentryBreadcrumb({
    category: "access-engine",
    message: `${component}: ${label} role=${result.role}`,
    data: logData,
    level: result.status === "denied" ? "warning" : "info",
  });
}
